/**
 * Evrella, as mounted inside the signed-in console: the real Evrella chat widget
 * (shared-chatbot packages/chat-widget), the one every Evrella website runs, so
 * a change to how she draws a reply reaches this page too.
 *
 * She has two brains, chosen by context:
 *
 * - Logged out (www.systemdox.com, published KB portals) she talks to the
 *   shared chatbot backend, grounded in SystemDox's public marketing content.
 * - Logged in (here) she talks to SystemDox's own API, which answers from the
 *   caller's documents with sources, and declines when it retrieves nothing.
 *
 * `authorize` is what makes this safe: the widget draws only the conversation
 * (no human check, no polling, no public call), and `POST /chat/message` takes
 * the tenant from the bearer token, never from the `tenantId` the widget sends.
 * The reply arrives as Markdown with its links made (common/citations.py).
 */

import { useCallback } from 'react';
import { ChatWidget } from '@puglieseweb/chat-widget';
import { AssistBar } from '@puglieseweb/shared-astro-ui/components/AssistBar';
import { useTenant } from '@/contexts/TenantContext';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';
import { aiApi } from '@/services/aiApi';

/** Distinct from puglieseweb.com's keys: different product, different prefs. */
const FONT_SIZE_KEY = 'sdx-font-size';
const COLLAPSED_KEY = 'sdx-assistbar-collapsed';

const AVATAR_PATH = '/ema-avatar.png';

export function EmaAssistant() {
  const { tenant } = useTenant();
  const authorize = useCallback(() => aiApi.authHeaders(), []);

  return (
    <>
      <AssistBar
        avatarUrl={AVATAR_PATH}
        // Clear of the BottomNav (~3.5rem tall) so the launcher does not sit
        // on top of the quick-access bar.
        offsetBottom="4.75rem"
        chatLabel="Evrella"
        fontSizeStorageKey={FONT_SIZE_KEY}
        collapsedStorageKey={COLLAPSED_KEY}
      />
      {tenant && (
        <ChatWidget
          // A conversation is the workspace's: switching tenant starts a new one.
          key={tenant.tenantId}
          apiUrl={SYSTEMDOX_API_URL}
          tenantId={tenant.tenantId}
          authorize={authorize}
          fetchConfig={false}
          businessName="Evrella"
          greeting="Hi! I'm Evrella. Ask me anything about this workspace's documentation. I answer from the documents and show you where I found it."
          // The widget takes an absolute address only, and drops anything else.
          avatarUrl={`${window.location.origin}${AVATAR_PATH}`}
          errorMessage="Sorry, I couldn't reach the assistant. Please try again."
          externalTrigger
          // The panel sits beside the toolbar, not over it.
          panelBeside=".assistbar-root"
          // The console's language, not the browser's: the widget would follow
          // the visitor's otherwise, and this app is in English.
          language="en"
          // Document questions, not workspace-metadata questions: retrieval
          // answers from document TEXT, so "which specs are stale?" (a property
          // of rows, not of prose) would only ever earn a decline.
          defaultSuggestions={[
            'How do I roll back a deployment?',
            'What is our authentication design?',
            'How is the data model structured?',
          ]}
        />
      )}
    </>
  );
}

export default EmaAssistant;
